import React, { useState, useContext, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useForm, Controller } from 'react-hook-form';
import ProductModal from './productOrderModal';
import PlacesAutocomplete from './inputs/PlacesAutocomplete';
import AddressDisplay from '../components/addressDisplay';
import {
  Dialog,
  Typography,
  Box,
  Grid,
  SvgIcon,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  CardMedia,
  ButtonBase,
} from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import { useOrder } from "../context/orderContext";
import { CatalogContext } from '../context/catalogContext';
import { UserContext } from '../context/userContext'

import {
  patchOrder as apiPatchOrder,
  createWebpayTransaction,
  createBinanceTransaction,
  applyDiscountCode
} from '../services/services';
import { useStoreSettings } from '../hooks/useStoreSettings';

// Utilities
const addThousandsSeparator = x => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const toRad = deg => (deg * Math.PI) / 180;
function haversine_distance({ lat: lat1, lng: lon1 }, { lat: lat2, lng: lon2 }) {
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const FormContainer = styled(Box)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  background: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

export default function Checkout() {
  const { userAddress, setUserAddress } = useContext(UserContext);
  const catalog = useContext(CatalogContext);
  const { handleSubmit, control, watch, formState: { errors } } = useForm();
  // Store settings hook
  const { deliveryRadius, isDeliveryAvailable } = useStoreSettings();
  // Order initialization and creation hook
  const { order, subtotal, totalAmount, shippingCost, setOrder, deliveryType, setDeliveryType, discountValue } = useOrder()

  // Encapsulated order logic

  const [modalMode, setModalMode] = useState('cart');
  const [paymentMethod, setPaymentMethod] = useState('webpay');
  const [transactionResponse, setTransactionResponse] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Para aplicar código
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState('');


  // Handle transaction response
  React.useEffect(() => {
    if (transactionResponse && paymentMethod === 'webpay') {
      document.forms.pay?.submit();
    }
  }, [transactionResponse, paymentMethod]);

  const onSubmit = useCallback(
    async data => {
      setPaymentLoading(true);
      try {
        const patched = await apiPatchOrder({
          ...order,
          ...data,
          discountCodeId: order.discount?.id,
          shippingCost,
          status: 'Waiting Payment',
        });
        setOrder(patched);

        let tx;
        if (paymentMethod === 'webpay') {
          tx = await createWebpayTransaction({ buyOrder: order.idSellOrder, amount: totalAmount });
        } else {
          tx = await createBinanceTransaction({ orderId: order.idSellOrder, orderAmount: totalAmount });
          window.location.href = tx.data.checkoutUrl;
        }
        setTransactionResponse(tx);
      } catch (err) {
        console.error('Error processing payment:', err);
      } finally {
        setPaymentLoading(false);
      }
    },
    [order, paymentMethod, shippingCost, totalAmount, setOrder]
  );
  const handleApplyDiscount = async () => {
    const code = watch('discountCode');
    if (!code) {
      setApplyError('discountCode', { type: 'required', message: 'Ingresa un código' });
      return;
    }
    setApplying(true);
    setApplyError('');
    try {
      const { success, discount } = await applyDiscountCode(order.idSellOrder, code);
      if (!success) throw new Error('No se pudo aplicar');
      setApplied(true);
      setOrder(prev => ({ ...prev, discount: discount }));
    } catch (err) {
      console.error(err);
      setApplyError(err.message || 'Código inválido');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <FormContainer>
        {/* Product detail modal */}
        {selectedProduct && (
          <Dialog
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ProductModal
              product={selectedProduct}
              mode={modalMode}
              menuData={catalog}
              order={order}
              setOrder={setOrder}
              onClose={() => setIsModalOpen(false)}
            />
          </Dialog>
        )}

        {/* Title & separator */}
        <Typography variant="h4" align="center" gutterBottom>
          Tu Orden
        </Typography>
        <Box display="flex" justifyContent="center" mb={2}>
          <SvgIcon viewBox="0 0 297.49 22.68" sx={{ width: 400 }}>
            <path d="M281,14a99.87,99.87,0,0,1-11.71-.71c4.05-.42,8.28-1.64,10.48-5-3.87-1.11-9.12,1.66-11.05,5h0c-3.76-.43-7.51-1-11.24-1.52,3.14-.85,6.09-2.4,7.39-4.56a12,12,0,0,0-10.76,4.09l-1.7-.22c-3.2-.4-6.44-.73-9.68-.87,3.19-1.45,6.06-3.71,6.94-6.87-3.22,0-8,3.33-8.94,6.81-1.6,0-3.2,0-4.79.09-6.11.38-12.15,1.68-18.25,2.26-4.58.43-8.8-.64-13.09-1.87-.23-2.3-1.52-4.56-4.34-6.43a21.35,21.35,0,0,0-19.51-1.85c-4.14,1.75-7.75,4.37-11.45,6.85a5.62,5.62,0,0,0,.34-5.22c-1.77-3.67-9.22-6-11.08-1.32-.19.49.47.75.8.47,2.14-1.86,6.33-.56,6.76,2.33.31,2.08-1.24,4.06-2.65,5.39-2,1.91-4.93,2.54-7.61,2.66a10,10,0,0,1-4.52-.86,3,3,0,0,1-1.77-1.73,5.24,5.24,0,0,0,0-1.48c.34-.91,1.26-1.4,1.94-.54.27.34.9,0,.74-.42-.54-1.31-1.53-1.94-2.89-1.32a2.72,2.72,0,0,0-.64.45,2.47,2.47,0,0,0-.63-.45c-1.36-.62-2.35,0-2.89,1.32-.16.4.47.76.73.42.69-.86,1.61-.37,2,.54a5.24,5.24,0,0,0,0,1.48,3.05,3.05,0,0,1-1.77,1.73,10,10,0,0,1-4.52.86c-2.68-.12-5.6-.75-7.62-2.66-1.41-1.33-2.95-3.31-2.64-5.39.43-2.89,4.62-4.19,6.76-2.33.33.28,1,0,.8-.47-1.86-4.7-9.31-2.35-11.08,1.32a5.62,5.62,0,0,0,.34,5.22c-3.7-2.48-7.31-5.1-11.46-6.85a21.33,21.33,0,0,0-19.5,1.85C94.4,6,93.11,8.29,92.88,10.59c-4.29,1.23-8.52,2.3-13.09,1.87-6.1-.58-12.14-1.88-18.25-2.26-1.59-.1-3.19-.12-4.79-.09C55.79,6.63,51,3.3,47.81,3.3c.88,3.16,3.75,5.42,6.94,6.87-3.24.14-6.48.47-9.68.87l-1.7.22A12,12,0,0,0,32.61,7.17c1.3,2.16,4.24,3.71,7.39,4.56-3.74.53-7.49,1.09-11.24,1.52h0c-1.93-3.31-7.18-6.08-11-5,2.2,3.4,6.43,4.62,10.48,5A99.87,99.87,0,0,1,16.46,14C11.77,11.52,5.22,10.08,0,11.32c4.47,5.26,11.69,4,17.85,3.88a86.66,86.66,0,0,0,14.32-.1c-3.82,1.3-10.1,4.36-10.34,7,4.42-.56,11.6-1.94,12.44-7.18,3.54-.33,7.1-.77,10.71-1.2-3.11,1.57-6,4.1-6.57,7,4.21,0,10-3.1,11.51-7.53a62.45,62.45,0,0,1,13.18-.11l1.78.23c-4.42.74-9.67,2.37-11.54,6.59,4.84,0,11-1.85,13.6-6.28,4.29.68,8.54,1.58,12.85,2.16a34.9,34.9,0,0,0,13.86-.84c2.88,6.55,12.81,11.28,17.78,4.3,1.47-2.07-2-4.15-3.43-2-3.39,5.19-10.77,2.1-12.52-2.81,5.2-1.57,10.39-2.87,15.88-2.73,7.69.19,15.31,2.24,23,3.17l.29.14c3.91,1.79,11.46,2.31,14.13-1.91,2.68,4.22,10.23,3.7,14.14,1.91l.29-.14c7.65-.93,15.27-3,23-3.17,5.49-.14,10.68,1.16,15.88,2.73-1.75,4.91-9.13,8-12.53,2.81-1.4-2.15-4.89-.07-3.42,2,5,7,14.9,2.25,17.78-4.3a34.89,34.89,0,0,0,13.86.84c4.31-.58,8.56-1.48,12.85-2.16,2.61,4.43,8.76,6.28,13.6,6.28C242.28,15.67,237,14,232.61,13.3l1.78-.23a62.45,62.45,0,0,1,13.18.11c1.55,4.43,7.29,7.53,11.5,7.53-.54-2.89-3.45-5.42-6.56-7,3.6.43,7.17.87,10.71,1.2.84,5.24,8,6.62,12.44,7.18-.24-2.64-6.52-5.7-10.35-7a86.67,86.67,0,0,0,14.32.1c6.17.13,13.39,1.38,17.86-3.88C292.27,10.08,285.72,11.52,281,14ZM111.36,8A50.16,50.16,0,0,0,95.65,9.8c2.22-5.12,9.4-8.61,14.76-8.15,7.09.59,12.28,5.37,18,9A88.33,88.33,0,0,0,111.36,8Zm74.77,0a88.08,88.08,0,0,0-17,2.73c5.7-3.66,10.88-8.44,18-9,5.36-.46,12.54,3,14.76,8.15A50.16,50.16,0,0,0,186.13,8Z" />
          </SvgIcon>
        </Box>

        {/* “Lleva también” complementary products */}
        <Box mt={3}>
          <Typography variant="h5" align="center" gutterBottom>
            Lleva también:
          </Typography>
          <Grid container spacing={1}>
            {catalog
              .find(cat => cat.name === 'Bebidas')
              ?.Products.filter(p =>
                ['Sanpellegrino', 'Bebida Lata Coca Cola', 'Bebida o Néctar Individual']
                  .includes(p.name)
              )
              .map(bebida => (
                <Grid
                  key={bebida.name}
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <ButtonBase
                    sx={{ width: '100%' }}
                    onClick={() => {
                      setSelectedProduct({ ...bebida, menu: 'Bebidas' });
                      setIsModalOpen(true);
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={bebida.Product_Images?.[0]?.publicUrl}
                      sx={{ height: 120, borderRadius: 1 }}
                    />
                  </ButtonBase>
                </Grid>
              ))}
            {/* fixed dessert */}
            <Grid
              size={{
                xs: 6,
                md: 3
              }}>
              <ButtonBase
                sx={{ width: '100%' }}
                onClick={() => {
                  const menu = catalog.find(c => c.name === 'Menú Postres');
                  const gelato = menu.Products.find(p => p.name === 'Gelato Artesanal Italiano');
                  setSelectedProduct({ ...gelato, menu: menu.name });
                  setIsModalOpen(true);
                }}
              >
                <CardMedia
                  component="img"
                  image="https://storage.googleapis.com/product-gallery/gelato.png"
                  sx={{ height: 120, borderRadius: 1 }}
                />
              </ButtonBase>
            </Grid>
            {catalog
              .find(cat => cat.name === 'Menú Acompañamientos')
              ?.Products.map(acc => (
                <Grid
                  key={acc.name}
                  size={{
                    xs: 6,
                    md: 3
                  }}>
                  <ButtonBase
                    sx={{ width: '100%' }}
                    onClick={() => {
                      setSelectedProduct({ ...acc, menu: 'Menú Acompañamientos' });
                      setIsModalOpen(true);
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={acc.Product_Images?.[0]?.publicUrl}
                      sx={{ height: 120, borderRadius: 1 }}
                    />
                  </ButtonBase>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* Delivery / Pickup selector */}
        <FormControl component="fieldset" sx={{ mt: 4, width: '100%', alignItems: 'center' }}>
          <FormLabel>Selecciona una opción</FormLabel>
          <Controller
            name="deliveryType"
            control={control}
            defaultValue="Retiro en Tienda"
            rules={{
              required: 'Requerido',
              validate: {
                delivery: v => !(v === 'Despacho a Domicilio' && !isDeliveryAvailable)
                  || 'Despacho deshabilitado'
              }
            }}
            render={({ field }) => (
              <RadioGroup
                row
                {...field}
                onChange={e => {
                  field.onChange(e.target.value);
                  setDeliveryType(e.target.value);
                }}
     
              >
                <FormControlLabel
                  value="Retiro en Tienda"
                  control={<Radio />}
                  label="Retiro en Tienda"
                />
                <FormControlLabel
                  value="Despacho a Domicilio"
                  control={<Radio />}
                  label="Despacho a Domicilio"
                />
              </RadioGroup>
            )}
          />
          {errors.deliveryType && (
            <Typography color="error">{errors.deliveryType.message}</Typography>
          )}
        </FormControl>

        {/* Address input if delivery */}
        {deliveryType === 'Despacho a Domicilio' && (
          <Box sx={{ mt: 2 }}>
            <Controller
              name="address"
              control={control}
              defaultValue={userAddress.fullAddress || ''}
              rules={{
                required: 'Dirección requerida',
                validate: v =>
                  haversine_distance(userAddress, { lat: -33.4376397, lng: -70.6158242 })
                  <= deliveryRadius / 1000
                  || `Máximo ${deliveryRadius / 1000} km`
              }}
              render={({ field }) => (
                <PlacesAutocomplete
                  value={field.value}
                  setValue={field.onChange}
                  setClientLocation={setUserAddress}
                  error={errors.address}
                />
              )}
            />
            {errors.address && (
              <Typography color="error">{errors.address.message}</Typography>
            )}
            <Box sx={{ mt: 1 }}>
              <AddressDisplay clientLocation={userAddress} />
            </Box>
          </Box>
        )}

        {/* Contact & discount fields */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {[
            { name: 'additionalInfo', label: 'Instrucciones Adicionales', multiline: true },
            { name: 'name', label: 'Nombre', rules: { required: 'Requerido' } },
            {
              name: 'email',
              label: 'Email',
              rules: {
                required: 'Requerido',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Correo inválido'
                }
              }
            },
            {
              name: 'phone',
              label: 'Teléfono',
              rules: {
                required: 'Requerido',
                pattern: {
                  value: /^(\+?56)?\d{9}$/,
                  message: '+56912345678'
                }
              }
            }
          ].map(({ name, label, rules, multiline }) => (
            <Grid key={name} size={12}>
              <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={label}
                    multiline={!!multiline}
                    error={!!errors[name]}
                    helperText={errors[name]?.message}
                  />
                )}
              />
            </Grid>
          ))}
        </Grid>

        {/* Order items */}
        <List sx={{ mt: 3 }}>
          {order.Order_Items.map((item, idx) => (
            <ListItem
              key={idx}
              divider
              button
              onClick={() => {
                setSelectedProduct({
                  ...item.Variant,
                  orderIndex: idx,
                  idProduct: item.Variant.Product.idProduct
                });
                setModalMode('edit');
                setIsModalOpen(true);
              }}
            >
              {/* Contenedor con Flexbox */}
              <Box
                component="div"
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {/* Columna 1 */}
                <Box sx={{ flex: 1 }}>
                  <Typography>×{item.quantity}</Typography>
                </Box>

                {/* Columna 2 */}
                <Box sx={{ flex: 8, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EditIcon sx={{ mr: 1 }} />
                    <Typography>
                      {item.Variant.Product.name} – {item.Variant.name}
                      {item.modifier ? ' Precocida' : ''}
                    </Typography>
                  </Box>
                  {item.Addition_Items?.length > 0 && (
                    <Box sx={{ ml: 4 }}>
                      {item.Addition_Items.map(add => (
                        <Typography
                          key={add.idAdditionItems}
                          variant="body2"
                          color="text.secondary"
                        >
                          + {add.name} (${add.price})
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Columna 3 (alineada a la derecha) */}
                <Box sx={{ flex: 3, textAlign: 'right' }}>
                  <Typography>
                    ${addThousandsSeparator(
                      (item.Variant.price +
                        item.Addition_Items.reduce((a, b) => a + b.price, 0)) *
                      item.quantity
                    )}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>



        {/* Totals */}
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography>Subtotal: ${addThousandsSeparator(subtotal)}</Typography>

          {order.discount && (
            <Typography color="success.main">
              {order.discount.name} {' '}
              {order.discount.discountAmount != null
                ? `(-$${addThousandsSeparator(discountValue)})`      // monto fijo
                : `(-${order.discount.discountPercentage}%): -$${addThousandsSeparator(discountValue)}` // porcentaje
              }
            </Typography>
          )}

          <Typography>Despacho: ${addThousandsSeparator(shippingCost)}</Typography>
          <Typography fontWeight="bold">
            Total: ${addThousandsSeparator(totalAmount)}
          </Typography>
        </Box>
        <Grid sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Controller
            name="discountCode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mr: 2 }}
                label="Código de Descuento"
                fullWidth
                disabled={applied}
                error={!!errors.discountCode}
                helperText={errors.discountCode?.message}
                value={field.value?.toUpperCase() || ''}
                onChange={e => field.onChange(e.target.value.toUpperCase())}
              />
            )}
          />
          <Button
            size="small"
            variant={applyError ? 'outlined' : 'contained'}
            color={applyError ? 'error' : applied ? 'success' : 'primary'}
            onClick={handleApplyDiscount}
            disabled={applying || applied}
            startIcon={
              applying
                ? <CircularProgress size={20} color="inherit" />
                : applied
                  ? <CheckCircleIcon />
                  : <CheckCircleIcon />
            }
          >
            {applying
              ? 'Aplicando…'
              : applied
                ? '¡Aplicado!'
                : applyError
                  ? 'Reintentar'
                  : 'Aplicar código'
            }
          </Button>
        </Grid>
        {/* Payment method */}
        <FormControl component="fieldset" sx={{ mt: 3 }}>
          <FormLabel>Medio de Pago</FormLabel>
          <RadioGroup
            row
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
          >
            <FormControlLabel
              value="webpay"
              control={<Radio />}
              label={<img src={require('../asset/WebpayPlus_FB_300px.png')} alt="webpay-logo" height="40" />}
            />
            <FormControlLabel
              value="binance"
              control={<Radio />}
              label={<img src={require('../asset/pay-blk200px.png')} alt="binance-logo" height="40" />}
            />
          </RadioGroup>
        </FormControl>

        {/* Hidden form for webpay */}
        {transactionResponse && paymentMethod === 'webpay' && (
          <form name="pay" action={transactionResponse.url} method="post">
            <input type="hidden" name="token_ws" value={transactionResponse.token} />
          </form>
        )}

        {/* Pay button */}
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          sx={{ mt: 3, fontWeight: 'bold', color: 'white' }}
          onClick={handleSubmit(onSubmit)}
          disabled={paymentLoading}
        >
          {paymentLoading ? <CircularProgress color="inherit" /> : 'Pagar'}
        </Button>
      </FormContainer>
    </Box>
  );
}